import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { LoaderWidget, printerStatus, useI18n } from '@jarvis/react-portal-addons';
import StatusList from './StatusList';
import { PRINTER_STATUS, getDeviceStatusCounts } from '../../../utils/printerStatus';
import { USERS_STATUS, getUsersStatusCounts } from '../../../utils/usersStatus';

import {
  CardBody,
  CardHeader,
  Container,
  StatusLoader,
  Title,
} from './styles';

import {
  StatusTotalPrintersClicked,
  StatusTotalUsersClicked,
  getModuleDisplayedEvent,
  getModuleInitializedEvent,
  publishEvent,
} from '../../../utils/analytics';

const StatusCard = ({ deviceCache, usersApi, consolidatedPortal }) => {
  const {
    pending: isLoadingDevices,
    error: devicesError,
    data: devices,
  } = consolidatedPortal ? deviceCache : { pending: false, data: [] };
  const {
    pending: isLoadingUsers,
    error: usersError,
    data: usersData,
  } = usersApi;

  const { t } = useI18n();

  const handleRetryDevices = useCallback(() => {
    deviceCache?.forceFetch();
  }, [deviceCache]);

  const handleRetryUsers = useCallback(() => {
    usersApi?.forceFetch();
  }, [usersApi]);

  const showStatusWidget = useMemo(
    () => !isLoadingDevices, [isLoadingDevices],
  );

  const deviceStatusCounts = useMemo(
    () => getDeviceStatusCounts(devices || []),
    [devices],
  );

  const hasLegacyDevice = useMemo(
    () => (isLoadingDevices
      ? false
      : devices?.some(device => printerStatus.isGen1OrGen2(device?.identity?.platformIdentifier))
    ),
    [devices, isLoadingDevices],
  );

  const usersStatusCounts = useMemo(
    () => getUsersStatusCounts(usersData || []),
    [usersData],
  );

  const statusItemsDevices = useMemo(() => PRINTER_STATUS.filter(item => item.id !== 'connected' || hasLegacyDevice)
    .map(item => ({
      ...item,
      titleText: t(item.title),
      value: deviceStatusCounts[item.id],
    })), [deviceStatusCounts, hasLegacyDevice, t]);

  const statusItemsUsers = useMemo(() => USERS_STATUS.map(item => ({
    ...item,
    titleText: t(item.title),
    value: usersStatusCounts[item.id],
  })), [usersStatusCounts, t]);

  useEffect(() => {
    if (consolidatedPortal && isLoadingDevices) {
      publishEvent(getModuleInitializedEvent('ReactEcpStatusWidget'));
    }
  }, [consolidatedPortal, isLoadingDevices]);

  useEffect(() => {
    if (showStatusWidget) {
      publishEvent(getModuleDisplayedEvent('/ReactEcpStatusWidget/', 'Status'));
    }
  }, [showStatusWidget]);

  if (!showStatusWidget && consolidatedPortal) {
    return (
      <StatusLoader>
        <LoaderWidget dataTestId="status-widget-loader" />
      </StatusLoader>
    );
  }

  return (
    <Container data-testid="status-widget-card">
      <CardHeader data-testid="status-widget-card-header">
        <Title className="subtitle-large" data-testid="status-widget-card-title">
          {t('status.header.title')}
        </Title>
      </CardHeader>
      <CardBody data-testid="status-widget-card-list-content">
        <StatusList
          analyticsEvents={StatusTotalPrintersClicked}
          dataTestId="status-widget-printers"
          handleRetry={handleRetryDevices}
          items={statusItemsDevices}
          isError={devicesError}
          isLoading={isLoadingDevices}
          redirectPath="/printers"
          subtitle={t('status.header.devices.totalPrinters')}
          totalCount={deviceStatusCounts.totalDevices}
        />
        <StatusList
          analyticsEvents={StatusTotalUsersClicked}
          dataTestId="status-widget-users"
          handleRetry={handleRetryUsers}
          items={statusItemsUsers}
          isError={usersError}
          isLoading={isLoadingUsers}
          redirectPath="/users"
          subtitle={t('status.header.users.total')}
          totalCount={usersStatusCounts.totalUsers}
        />
      </CardBody>
    </Container>
  );
};

StatusCard.propTypes = {
  consolidatedPortal: PropTypes.bool.isRequired,
  deviceCache: PropTypes.shape({
    pending: PropTypes.bool,
    data: PropTypes.arrayOf(PropTypes.object),
    error: PropTypes.shape({
      message: PropTypes.string,
      code: PropTypes.number,
    }),
    isFetching: PropTypes.bool,
    forceFetch: PropTypes.func,
    makeApiCall: PropTypes.func,
    success: PropTypes.bool,
  }).isRequired,
  usersApi: PropTypes.shape({
    pending: PropTypes.bool,
    data: PropTypes.arrayOf(PropTypes.object),
    error: PropTypes.shape({
      message: PropTypes.string,
      code: PropTypes.number,
    }),
    isFetching: PropTypes.bool,
    forceFetch: PropTypes.func,
    makeApiCall: PropTypes.func,
    success: PropTypes.bool,
  }),
};

StatusCard.defaultProps = {
  usersApi: undefined,
};

export default StatusCard;
